import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { linkOptions } from '@tanstack/react-router';
import { useReportPermissions } from '@anytag/auth';
import { SpreadButton } from '@anytag/shared-molecules';
import { TikTokSpecialCampaign } from '@anytag/graphql';
import { TableCell } from '@anytag/shared-atoms';
import { localizedDateFormatter } from '@anytag/shared-utils';
import { useTikTokCampaignRoutes } from '../../../hooks';
import { TikTokCampaignStatus } from '../../TikTokCampaignStatus';
import { TikTokCampaignTabsType, getAccessTikTokCampaignTab } from '../../TikTokCampaignTabs';

export const useTikTokCampaignsTable = () => {
  const { t } = useTranslation();
  const { hideReportLink } = useReportPermissions();
  const { getTikTokCampaignDetailsRoute } = useTikTokCampaignRoutes();

  const columns: ColumnDef<TikTokSpecialCampaign>[] = [
    {
      accessorKey: 'title',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Campaign Title'),
      cell: ({ row }) => {
        const { id, title, status } = row.original;
        const getDetailsUrl = (tab: keyof typeof TikTokCampaignTabsType) =>
          linkOptions({ to: getTikTokCampaignDetailsRoute(tab), params: { id: id.toString() } });
        const detailsUrl = getDetailsUrl('Details');

        return (
          <div className="campaign-title-column">
            <TableCell.Link label={title} className="text-sm" to={detailsUrl.to} params={detailsUrl.params} />
            <SpreadButton
              collapseFrom={2}
              css={{ marginLeft: '4px' }}
              options={[
                {
                  icon: 'post',
                  label: t('Posts'),
                  to: getDetailsUrl('Posts').to,
                  params: getDetailsUrl('Posts').params,
                  tooltip: { help: t('Button.Posts') },
                  disabled: !getAccessTikTokCampaignTab('Posts', status)
                },
                {
                  icon: 'chart',
                  label: t('Report'),
                  hidden: hideReportLink,
                  to: getDetailsUrl('Report').to,
                  params: getDetailsUrl('Report').params,
                  tooltip: { help: t('Button.Report') },
                  disabled: !getAccessTikTokCampaignTab('Report', status)
                }
              ]}
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'plan',
      header: t<string>('HeaderColumn.Plan'),
      cell: ({ row }) => <TableCell.Text value={t(`Plan.${row.original.plan}`)} />
    },
    {
      accessorKey: 'advertiser',
      header: t<string>('HeaderColumn.Advertiser'),
      cell: ({ row }) => <TableCell.Text value={row.original.advertiser.name || '-'} />
    },
    {
      accessorKey: 'status',
      header: t<string>('HeaderColumn.Status'),
      cell: ({ row }) => <TikTokCampaignStatus status={row.original.status} />
    },
    {
      accessorKey: 'preLaunchDate',
      header: t<string>('HeaderColumn.Pre Launch date'),
      cell: ({ row }) => (
        <TableCell.Text value={localizedDateFormatter(row.original.preLaunchDate || null, 'yyyy/MM/dd')} />
      )
    },
    {
      accessorKey: 'campaignDuration',
      header: t<string>('HeaderColumn.Campaign Duration'),
      cell: ({ row }) => (
        <TableCell.Period period={{ startDate: row.original.startDate, endDate: row.original.endDate }} />
      )
    },
    {
      accessorKey: 'joinedInfluencersCount',
      header: t<string>('HeaderColumn.Joined Influencers'),
      cell: ({ row }) => (
        <TableCell.Text value={`${row.original.joinedInfluencersCount}/${row.original.maxNumberInfluencers}`} />
      )
    }
  ];

  return { columns };
};
